// Feb 18, 2026 — Role-aware visibility infrastructure.
//
// Single source of truth for "is this user a Creator? An Affiliate? A
// Super Admin?" Instead of scattered `if (user.is_affiliate)` checks
// across the site, every gated card wraps itself in <RoleGate>.
//
// Flags on the user doc:
//   role: "creator" | "super_admin"   — base identity
//   is_affiliate: bool                — paid the $300 gate
//   affiliate_tier: "base" | "performance"  — auto-derived
//
// Gate values accepted by <RoleGate role="...">:
//   "creator"        — any logged-in creator (covers everyone except anon)
//   "affiliate"      — affiliates only (is_affiliate === true)
//   "non_affiliate"  — creators who HAVEN'T upgraded (Creator only, no gate paid)
//   "performance"    — affiliates at the performance tier
//   "super_admin"    — super admin only
import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";

export function useRoles() {
  const [user, setUser] = useState(() => getUser());
  const [loading, setLoading] = useState(!user);

  useEffect(() => {
    if (user) { setLoading(false); return; }
    let cancel = false;
    api.get("/auth/me")
      .then((r) => { if (!cancel) setUser(r.data); })
      .catch(() => { /* anon */ })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  const isSuperAdmin = !!user?.is_super_admin || user?.role === "super_admin";
  const isCreator = !!user && (user.role === "creator" || user.role === "super_admin");
  // Super admins are inherently affiliates (founder = first member, top tier) —
  // they never paid the $300 gate but always qualify, so no upsells ever show.
  const isAffiliate = !!user?.is_affiliate || isSuperAdmin;
  const isPerformanceTier = isAffiliate && (user?.affiliate_tier === "performance" || isSuperAdmin);
  const isNonAffiliate = isCreator && !isAffiliate;

  return { user, loading, isCreator, isAffiliate, isPerformanceTier, isSuperAdmin, isNonAffiliate };
}

export function RoleGate({ role, fallback = null, children }) {
  const r = useRoles();
  if (r.loading) return null;
  const allow = (() => {
    switch (role) {
      case "creator": return r.isCreator;
      case "affiliate": return r.isAffiliate;
      case "non_affiliate": return r.isNonAffiliate;
      case "performance": return r.isPerformanceTier;
      case "super_admin": return r.isSuperAdmin;
      default: return true;
    }
  })();
  return allow ? <>{children}</> : fallback;
}

export default useRoles;
